// Firestore listener wrapper that reports health signals to Vuex.
//
// Drop-in replacement for `query.onSnapshot(onNext, onError)` that also:
//   - increments a "pending first snapshot" counter while awaiting the first
//     callback (informational for diagnostics)
//   - reports listener errors to `connectionHealth/recordListenerError` so
//     the centralised state can flip backend to `degraded` on repeated
//     unavailable / deadline-exceeded / cancelled / internal errors
//   - clears `degraded` automatically when a successful snapshot arrives
//
// Usage:
//   const unsub = subscribeWithHealth(
//     fc.companies.doc(id).collection("DocsThaiSmartTax"),
//     (snap) => { ... },
//     {
//       source: "thaismartTaxDetail/getList",
//       onError: (err) => { ... }, // optional
//       includeMetadataChanges: false,
//     }
//   );
//   // Later:
//   unsub();
//
// The helper is intentionally opt-in. Migrating a listener is a one-line
// change at the call site; existing onSnapshot calls keep working unchanged.

// Lazy-import the store to avoid a circular module dependency: this file is
// imported by store collection files at module-eval time, but the default
// store export is only ready after Vuex finishes wiring all modules. We grab
// it on first use instead.
let _store = null;
function getStore() {
  if (_store) return _store;
  // eslint-disable-next-line global-require
  _store = require("@/store").default;
  return _store;
}

function safeDispatch(action, payload) {
  try {
    const store = getStore();
    if (store && typeof store.dispatch === "function") {
      store.dispatch(action, payload);
    }
  } catch (e) {
    // Never let a health-reporting failure break the listener itself.
    // eslint-disable-next-line no-console
    console.warn("[subscribeWithHealth] safeDispatch failed:", e);
  }
}

export function subscribeWithHealth(query, onNext, options) {
  const opts = options || {};
  const source = opts.source || "unknown";
  let receivedFirstSnapshot = false;
  let pendingReleased = false;

  const releasePending = () => {
    if (pendingReleased) return;
    pendingReleased = true;
    safeDispatch("connectionHealth/noteListenerReady");
  };

  safeDispatch("connectionHealth/notePendingListener");

  const next = (snap) => {
    if (!receivedFirstSnapshot) {
      receivedFirstSnapshot = true;
      releasePending();
    }
    safeDispatch("connectionHealth/noteSuccessfulSnapshot");
    try {
      onNext(snap);
    } catch (e) {
      // Surface the original error in dev — don't swallow domain logic bugs.
      // eslint-disable-next-line no-console
      console.error(`[subscribeWithHealth:${source}] onNext threw:`, e);
      throw e;
    }
  };

  const error = (err) => {
    releasePending();
    safeDispatch("connectionHealth/recordListenerError", {
      code: err && err.code ? err.code : "unknown",
      source,
    });
    if (typeof opts.onError === "function") {
      opts.onError(err);
    } else {
      // eslint-disable-next-line no-console
      console.warn(`[subscribeWithHealth:${source}] listener error:`, err);
    }
  };

  // Firestore v7 onSnapshot signatures:
  //   onSnapshot(onNext, onError)
  //   onSnapshot(options, onNext, onError)
  let unsub;
  if (opts.includeMetadataChanges) {
    unsub = query.onSnapshot(
      { includeMetadataChanges: true },
      next,
      error
    );
  } else {
    unsub = query.onSnapshot(next, error);
  }

  // Wrap unsub so we release the "pending" counter if the caller tears down
  // before the first snapshot ever arrived.
  return function unsubscribeWithHealth() {
    releasePending();
    if (typeof unsub === "function") unsub();
  };
}

export default subscribeWithHealth;
